import type { Choice, NumChoicesRequired, SkillTypes } from "@memorang/types";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useDevice } from "../../../../useDevice";
import AudioPlayerContainer from "../../../AudioPlayerContainer";
import Box from "../../../Box";
import type { ImageType } from "../../../ImagesCarousel";
import ImagesCarousel from "../../../ImagesCarousel";
import InfoAlert from "../../../InfoAlert";
import type { ItemAudioGate } from "../../types";
import ChoicesList from "../ChoicesList";
import DragDropBlanks from "../DragDropBlanks";
import ItemStem from "../ItemStem";
import MatchingPair from "../MatchingPair";
import PassageContent from "./PassageContent";
import type { TextCompletionField } from "./TextCompletion";
import TextCompletion from "./TextCompletion";
import { SubInstruction } from "./pte/SubInstruction";
import type { Slot } from "./toeic-layout-config";

export type ToeicItemData = {
	skill: SkillTypes;
	leftImages?: ImageType[];
	leftAudioUri?: string;
	leftPassage: string;
	passageTitle?: string;
	instruction: string;
	questionInstruction?: string;
	stem?: string;
	itemAudioUri?: string;
	itemImages?: ImageType[];
	isAnswered?: boolean;
	choices?: Choice[];
	selectedChoiceIds?: string[];
	correctAnswerIds?: string[];
	handleSelectChoice: (id: string) => void;
	isMultiSelect?: boolean;
	numChoicesRequired?: NumChoicesRequired;
	choiceInReviewMode?: boolean;
	shuffle?: boolean;
	values?: Record<string, string>;
	handleChangeValues: (values: Record<string, string>) => void;
	blankInReviewMode?: boolean;
	blankStem?: string;
	dropdownFields?: TextCompletionField[];
	dropdownCorrectValues?: Record<string, string>;
	wordBank?: Choice[];
	dragCorrectWordIdsByBlank?: Record<string, string>;
	matchingLeft?: Choice[];
	matchingRight?: Choice[];
	matchingLeftLabel?: string;
	matchingRightLabel?: string;
	matchingCorrectTokenIdBySlot?: Record<string, string>;
	currentStimulusItemIndex?: number;
	numTotalStimulusItems?: number;
};

type Props = {
	slot: Slot;
	data: ToeicItemData;
	itemId: number;
	isSummativeTest?: boolean;
	renderFeedback?: () => ReactNode;
	renderHint?: () => ReactNode;
	showTTS?: boolean;
	renderTTS?: () => ReactNode;
	audioGate?: ItemAudioGate;
	hideStimulusAudio?: boolean;
	renderInstructionAsAlert?: boolean;
	stimulusFullBleed?: boolean;
};

const ToeicSlot = ({
	slot,
	data,
	itemId,
	isSummativeTest,
	renderFeedback,
	renderHint,
	showTTS,
	renderTTS,
	audioGate,
	hideStimulusAudio,
	renderInstructionAsAlert,
	stimulusFullBleed,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const {
		leftPassage,
		instruction,
		leftAudioUri,
		leftImages,
		itemImages = [],
		itemAudioUri,
		questionInstruction = "",
		stem = "",
		isAnswered = false,
		choices = [],
		selectedChoiceIds = [],
		correctAnswerIds,
		handleSelectChoice,
		isMultiSelect = false,
		numChoicesRequired,
		choiceInReviewMode = false,
		shuffle = false,
		values = {},
		handleChangeValues,
		blankInReviewMode = false,
		blankStem = "",
		dropdownFields = [],
		dropdownCorrectValues,
		wordBank = [],
		dragCorrectWordIdsByBlank,
		matchingLeft = [],
		matchingRight = [],
		matchingLeftLabel,
		matchingRightLabel,
		matchingCorrectTokenIdBySlot,
		currentStimulusItemIndex,
		numTotalStimulusItems,
		passageTitle,
	} = data;

	switch (slot) {
		case "stimulus":
			return (
				<PassageContent
					passage={leftPassage}
					instruction={instruction}
					currentItemIndex={currentStimulusItemIndex}
					numTotalItems={numTotalStimulusItems}
					audioUri={hideStimulusAudio ? undefined : leftAudioUri}
					images={leftImages}
					passageTitle={passageTitle}
					audioGate={audioGate}
					fullBleed={stimulusFullBleed}
				/>
			);
		case "item_image":
			return itemImages.length > 0 ? (
				<ImagesCarousel images={itemImages} disableSingleImageSizeLimits />
			) : null;
		case "item_audio":
			return itemAudioUri ? (
				<AudioPlayerContainer
					ref={audioGate?.playerRef}
					uri={itemAudioUri}
					useReanimated={false}
					playOnce={audioGate?.playOnce}
					forceCompleted={audioGate?.forceCompleted}
					onEnd={audioGate?.onEnd}
				/>
			) : null;
		case "question_instruction": {
			if (!questionInstruction) {
				return null;
			}
			return renderInstructionAsAlert ? (
				<InfoAlert text={questionInstruction} />
			) : (
				<SubInstruction text={questionInstruction} />
			);
		}
		case "stem": {
			const showItemCount = Boolean(
				!isMobile && currentStimulusItemIndex && numTotalStimulusItems,
			);
			if (!(stem || showItemCount)) {
				return null;
			}
			return (
				<Box gap={2}>
					{showItemCount ? (
						<Text variant="bodySmall">
							{t("session.passageStem.itemOfTotal", {
								current: currentStimulusItemIndex,
								total: numTotalStimulusItems,
							})}
						</Text>
					) : null}
					{stem ? (
						<ItemStem text={stem} showTTS={showTTS} renderTTS={renderTTS} />
					) : null}
				</Box>
			);
		}
		case "choices":
			return (
				<ChoicesList
					choices={choices}
					handleSelectChoice={handleSelectChoice}
					selectedChoiceIds={selectedChoiceIds}
					correctAnswerIds={correctAnswerIds}
					isAnswered={isAnswered}
					itemId={itemId}
					shuffle={shuffle}
					multiSelect={isMultiSelect}
					numChoicesRequired={numChoicesRequired}
					isSummativeTest={isSummativeTest}
					inReviewMode={choiceInReviewMode}
					useCardWrapper
				/>
			);
		case "drag_drop":
			return (
				<DragDropBlanks
					stemText={blankStem}
					wordBank={wordBank}
					values={values}
					onChangeValues={handleChangeValues}
					disabled={isAnswered}
					isAnswered={isAnswered}
					inReviewMode={blankInReviewMode}
					correctWordIdsByBlank={
						isAnswered ? dragCorrectWordIdsByBlank : undefined
					}
				/>
			);
		case "dropdown_blanks":
			return (
				<TextCompletion
					stem={blankStem}
					fields={dropdownFields}
					values={values}
					onChangeValues={handleChangeValues}
					disabled={isAnswered}
					correctValues={dropdownCorrectValues}
				/>
			);
		case "matching_pair":
			return (
				<MatchingPair
					left={matchingLeft}
					right={matchingRight}
					leftLabel={matchingLeftLabel}
					rightLabel={matchingRightLabel}
					bankSide="right"
					values={values}
					onChangeValues={handleChangeValues}
					disabled={isAnswered}
					isAnswered={isAnswered}
					inReviewMode={blankInReviewMode}
					correctTokenIdBySlot={
						isAnswered ? matchingCorrectTokenIdBySlot : undefined
					}
				/>
			);
		case "hint":
			return renderHint?.() ?? null;
		case "feedback":
			return renderFeedback?.() ?? null;
		default:
			return null;
	}
};

export default ToeicSlot;
